import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { NavPreviewItem, NavPreviewItemsWrapper } from '@components/shared/SharedStyles';
import { SFDBodyMD } from '@components/shared/Typography';
import { PageOptions, Article, NavBarCategory, NavStatus, NavigationPreview, NavigationSection } from '@contracts';
import { getSetOfArticles } from '@prismicVitals';
import { NavContext } from './Context';
import NavLink from './NavLink';
import NavSearch from './NavSearch';

interface Props {
  periodCare: NavigationSection;
  vaginalHealth: NavigationSection;
  accessories: NavigationSection;
  aboutUs: NavigationSection;
  discover: NavigationSection;
  productCategories: NavigationPreview[];
  vitalsArticleUids: string[];
}

const NavMainMenuDesktop: React.FC<Props> = ({ periodCare, vaginalHealth, accessories, aboutUs, discover, productCategories, vitalsArticleUids }) => {
  const { t } = useTranslation();
  const { navState, navDispatch } = useContext(NavContext);
  const { navbarSelectedCategories } = navState;
  const [articles, setArticles] = useState<Article[]>([]);
  const isAboutUsOrDiscover = useMemo(
    () =>
      navbarSelectedCategories?.length
        ? navbarSelectedCategories.includes(NavBarCategory.AboutUs) || navbarSelectedCategories.includes(NavBarCategory.Discover)
        : false,
    [navbarSelectedCategories]
  );
  const isSearch = navbarSelectedCategories?.length ? navbarSelectedCategories.includes(NavBarCategory.Search) : false;

  useEffect(() => {
    if (!articles?.length && vitalsArticleUids?.length) {
      getSetOfArticles(setArticles, vitalsArticleUids.length, vitalsArticleUids);
    }
  }, []);

  const vitalArticlePreviews: NavigationPreview[] = useMemo(
    () =>
      articles?.length
        ? articles.map((article) => ({
            title: article.data.navigation_text,
            link: `/${PageOptions.Vitals}/${article.data.category?.uid}/${article.uid}`,
            image: article.data.navigation_image.url,
            imageAlt: article.data.navigation_image.alt,
            badgeText: article.data.category?.slug,
            badgeTextColor: Colors.navyBlue,
            badgeBackground: article.data.navigation_badge_color
          }))
        : [],
    [articles]
  );

  return (
    <DesktopMenuWrapper $isAboutUsOrDiscover={!isAboutUsOrDiscover} $isSearch={isSearch}>
      {isSearch ? (
        <NavSearch />
      ) : (
        <>
          <LeftLinks>
            {(isAboutUsOrDiscover ? [aboutUs, discover] : [periodCare, vaginalHealth, accessories]).map((section) => (
              <LeftLink key={`nav-link-wrapper${section.title}`}>
                <LeftLinkTitle>{t(section.title || '')}</LeftLinkTitle>
                <LeftLinkItems>
                  {section.items.map((item) => (
                    <NavLink
                      key={`nav-link-${item.title}`}
                      text={t(item.title)}
                      navLink={item.link}
                      isNew={item.is_new}
                      color={item.text_color}
                      isInternal
                    />
                  ))}
                </LeftLinkItems>
              </LeftLink>
            ))}
          </LeftLinks>
          <RightLinks>
            <NavPreviewItemsWrapper>
              {(isAboutUsOrDiscover ? vitalArticlePreviews : productCategories).map((preview) => (
                <NavPreviewItem
                  key={`nav-preview-link-${preview.link}`}
                  to={preview.link}
                  onClick={() => {
                    navDispatch({ type: 'setStatus', data: NavStatus.Default });
                    navDispatch({ type: 'removeNavbarSelectedCategories' });
                  }}
                >
                  <img src={preview.image} alt={preview.imageAlt} />
                  <PreviewBadge $color={preview.badgeTextColor} $backgroundColor={preview.badgeBackground}>
                    {t(preview.badgeText || '')}
                  </PreviewBadge>
                  <SFDBodyMD $bold $uppercase={isAboutUsOrDiscover}>
                    {t(preview.title)}
                  </SFDBodyMD>
                </NavPreviewItem>
              ))}
            </NavPreviewItemsWrapper>
          </RightLinks>
        </>
      )}
    </DesktopMenuWrapper>
  );
};

export default NavMainMenuDesktop;

const DesktopMenuWrapper = styled.div<{ $isAboutUsOrDiscover?: boolean; $isSearch: boolean }>`
  display: flex;
  justify-content: ${({ $isSearch }) => ($isSearch ? 'center' : 'space-between')};
  padding: 0 30px;

  @media (max-width: ${({ $isAboutUsOrDiscover }) => ($isAboutUsOrDiscover ? '1250px' : '1000px')}) {
    flex-direction: column;
    justify-content: ${({ $isSearch }) => (!$isSearch ? 'center' : 'space-between')};

    > div {
      align-self: center;

      &:last-child {
        margin-top: ${({ $isSearch }) => ($isSearch ? '0' : '40px')};
      }
    }
  }
`;

const LeftLinks = styled.div`
  display: flex;
  gap: 20px;
`;

const LeftLink = styled.div``;

const LeftLinkTitle = styled.div`
  color: ${Colors.navyBlue};
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  margin-bottom: 15px;
  cursor: default;
`;

const LeftLinkItems = styled.div`
  display: flex;
  flex-direction: column;

  a {
    padding-bottom: 10px;
  }
`;

const RightLinks = styled.div`
  max-width: 580px;
`;

const PreviewBadge = styled.div<{ $color?: string; $backgroundColor?: string }>`
  height: 26px;
  align-self: flex-start;
  color: ${({ $color }) => $color || Colors.navyBlue};
  background: ${({ $backgroundColor }) => $backgroundColor || Colors.mint};
  border-radius: 5px;
  display: flex;
  align-items: center;
  text-align: center;
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 18px */
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0 10px;
  text-decoration: none;
`;
